export const readinessTranslations = {
  en: {
    "seo.readiness.title": "Founder Readiness — Investor-Ready Narrative & Pitch | Peter Dingle",
    "seo.readiness.description": "A four-stage programme — Capture, Reframe, Pressure-Test, Perform — to build a defensible investor narrative and communicate your business under pressure.",

    "readiness.hero.stage": "Stage 1 · Founder Readiness",
    "readiness.hero.title": "Prepare Your Company for Serious Capital",
    "readiness.hero.body1": "Most founders don't fail because the business is weak.",
    "readiness.hero.body1b": "They fail because investors misunderstand it.",
    "readiness.hero.body2": "We work with founders to ensure their business is clearly understood, correctly positioned, and ready for institutional capital conversations.",
    "readiness.hero.cta": "Arrange a Confidential Founder Assessment",

    "readiness.problem.label": "The Problem",
    "readiness.problem.title": "Where Capital Is Lost",
    "readiness.problem.p1": "In most fundraising situations, the underlying business is not the problem.",
    "readiness.problem.p2": "The issue is how investors interpret it.",
    "readiness.problem.p3": "In the first minutes of a conversation, companies are often:",
    "readiness.problem.li1": "placed in the wrong category",
    "readiness.problem.li2": "misunderstood in scale",
    "readiness.problem.li3": "underestimated in opportunity",
    "readiness.problem.p4": "Once that misclassification happens, the conversation rarely recovers.",
    "readiness.problem.p5": "We address this before you enter the room.",

    "readiness.approach.label": "Our Approach",
    "readiness.approach.title": "A Structured Advisory Process",
    "readiness.approach.p1": "Our work is not about polishing a pitch deck. It is about reshaping how your business is understood.",
    "readiness.approach.p2": "We follow a disciplined four-stage process:",
    "readiness.approach.capture.title": "Capture",
    "readiness.approach.capture.body": "We start with a clear, unfiltered view of your business, without positioning, narrative framing, or presentation bias.",
    "readiness.approach.reframe.title": "Reframe",
    "readiness.approach.reframe.body": "We apply an investor lens to restructure how your company is positioned in terms of category, scale, and opportunity.",
    "readiness.approach.pressure.title": "Pressure-Test",
    "readiness.approach.pressure.body": "You rebuild the narrative and supporting materials, and we test clarity under real investor-style questioning.",
    "readiness.approach.perform.title": "Perform",
    "readiness.approach.perform.body": "You demonstrate the ability to communicate your business clearly, confidently, and consistently in live scenarios.",

    "readiness.outcomes.label": "What You Leave With",
    "readiness.outcomes.title": "What This Produces",
    "readiness.outcomes.intro": "By the end of this process, you will have:",
    "readiness.outcomes.li1": "A clear and defensible investor narrative",
    "readiness.outcomes.li2": "A structured and coherent pitch",
    "readiness.outcomes.li3": "Stronger control in investor conversations",
    "readiness.outcomes.li4": "The ability to articulate your opportunity under pressure",
    "readiness.outcomes.footer": "This is the foundation for effective capital raising.",

    "readiness.audience.label": "Who This Is For",
    "readiness.audience.title": "Who We Work With",
    "readiness.audience.intro": "This stage is designed for:",
    "readiness.audience.li1": "Pre-Series A founders preparing for their first institutional raise",
    "readiness.audience.li2": "Series A companies refining their investor positioning",
    "readiness.audience.li3": "Businesses that are not getting traction with investors despite strong fundamentals",

    "readiness.transition.label": "Transition",
    "readiness.transition.title": "Progression to Capital Raising",
    "readiness.transition.p1": "Not all companies proceed to Stage 2.",
    "readiness.transition.p2": "Progression is based on readiness.",
    "readiness.transition.p3": "Only when your narrative is clear, consistent, and capable of standing up to investor scrutiny do we move to capital engagement.",
    "readiness.transition.link": "Learn about Stage 2 · Capital Raising",

    "readiness.cta.title": "Arrange a Confidential Founder Assessment",
    "readiness.cta.body": "A private discussion to assess how investors are currently interpreting your business, and where narrative clarity may unlock capital.",
    "readiness.cta.button": "Book a Session",
    "readiness.cta.footer1": "The right narrative.",
    "readiness.cta.footer2": "The right investors.",
    "readiness.cta.footer3": "The right capital pathway across Asia-Pacific.",
  },
  zh: {
    "seo.readiness.title": "创始人融资准备 — 打造投资人认可的商业叙事 | Peter Dingle",
    "seo.readiness.description": "一个四阶段项目——梳理、重塑、压力测试、实战——帮助你建立经得起推敲的投资人叙事，在压力之下清晰表达业务。",

    "readiness.hero.stage": "第一阶段 · 创始人融资准备",
    "readiness.hero.title": "让公司做好迎接正式资本的准备",
    "readiness.hero.body1": "多数创始人失败，不是因为业务本身不行。",
    "readiness.hero.body1b": "而是因为投资人没能理解这门生意。",
    "readiness.hero.body2": "我们与创始人合作，确保业务能被清晰理解、准确定位，并为机构资本对话做好准备。",
    "readiness.hero.cta": "预约一次保密的创始人评估",

    "readiness.problem.label": "问题所在",
    "readiness.problem.title": "资本流失于何处",
    "readiness.problem.p1": "在大多数融资场景中，问题并不出在业务本身。",
    "readiness.problem.p2": "问题在于投资人如何解读这门生意。",
    "readiness.problem.p3": "在对话的最初几分钟里，公司往往会被：",
    "readiness.problem.li1": "归入错误的赛道",
    "readiness.problem.li2": "误判规模",
    "readiness.problem.li3": "低估机会",
    "readiness.problem.p4": "一旦出现这种误判，谈话很难再挽回。",
    "readiness.problem.p5": "我们要在你走进会议室之前，把这个问题解决掉。",

    "readiness.approach.label": "我们的方法",
    "readiness.approach.title": "一套结构化的顾问流程",
    "readiness.approach.p1": "我们的工作不是打磨一份路演材料，而是重塑外界理解你业务的方式。",
    "readiness.approach.p2": "我们遵循严谨的四个阶段：",
    "readiness.approach.capture.title": "梳理",
    "readiness.approach.capture.body": "我们首先获取一个清晰、未经修饰的业务全貌，不带任何定位包装、叙事美化或呈现偏见。",
    "readiness.approach.reframe.title": "重塑",
    "readiness.approach.reframe.body": "我们以投资人的视角重新构建公司的定位，涵盖赛道归属、规模判断与机会大小。",
    "readiness.approach.pressure.title": "压力测试",
    "readiness.approach.pressure.body": "你重新打磨叙事与支撑材料，我们则以真实投资人的提问方式，检验其清晰度。",
    "readiness.approach.perform.title": "实战",
    "readiness.approach.perform.body": "你要证明，自己能够在真实场景中清晰、自信、稳定地表达业务。",

    "readiness.outcomes.label": "你将获得什么",
    "readiness.outcomes.title": "这个过程能带来什么",
    "readiness.outcomes.intro": "完成这一过程后，你将拥有：",
    "readiness.outcomes.li1": "一套清晰且经得起推敲的投资人叙事",
    "readiness.outcomes.li2": "一份结构清晰、逻辑连贯的路演方案",
    "readiness.outcomes.li3": "在投资人对话中更强的主导力",
    "readiness.outcomes.li4": "在压力之下清晰阐述业务机会的能力",
    "readiness.outcomes.footer": "这是有效融资的基础。",

    "readiness.audience.label": "适合对象",
    "readiness.audience.title": "我们服务的对象",
    "readiness.audience.intro": "本阶段适合以下几类企业：",
    "readiness.audience.li1": "正在准备首轮机构融资的Pre-A轮创始人",
    "readiness.audience.li2": "正在完善投资人定位的A轮公司",
    "readiness.audience.li3": "基本面稳健，但迟迟得不到投资人青睐的企业",

    "readiness.transition.label": "衔接阶段",
    "readiness.transition.title": "迈向融资阶段",
    "readiness.transition.p1": "并非所有公司都会进入第二阶段。",
    "readiness.transition.p2": "能否推进，取决于准备程度。",
    "readiness.transition.p3": "只有当你的叙事清晰、连贯，并经得起投资人审视时，我们才会推进到资本对接阶段。",
    "readiness.transition.link": "了解第二阶段 · 融资对接",

    "readiness.cta.title": "预约一次保密的创始人评估",
    "readiness.cta.body": "一次私密的沟通，评估投资人当下如何理解你的业务，以及叙事的清晰度可能在哪里释放资本机会。",
    "readiness.cta.button": "预约会谈",
    "readiness.cta.footer1": "正确的叙事。",
    "readiness.cta.footer2": "正确的投资人。",
    "readiness.cta.footer3": "覆盖亚太的正确资本路径。",
  },
} as const;
